<template>
  <smart-filters
    v-model="smartFiltersModel"
    :schema="filterSchema"
    :default-filters="defaultFilters"
    storage-key="prices"
    @filter="filtrarCotizacion"
    @clear="LimpiarFiltros"
  />
</template>

<script setup>
import {
  computed,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import SmartFilters from '@/shared/components/smart_filters.vue';
import { usePricesStore } from '../stores/prices_store.js';
import { useAuthStore } from '@/views/auth/auth_store';
import { useEstadoSolicitudStore } from '@/stores/estado-solicitud.js';
import { useElementoTipoStore } from '@/stores/elemento_tipo/elemento_tipo_store.js';
import { useEjecutivoStore } from '@/stores/ejecutivo_store.js';
import { useUsabilidadStore } from '@/stores/usabilidad/usabilidad_store.js';
import { useProcesoStore } from '@/stores/proceso_store.js';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';
import prices_filters_model from '../models/prices_filters_model.js';

const priceStore = usePricesStore();
const authStore = useAuthStore();
const estadoSolicitudStore = useEstadoSolicitudStore();
const elementoTipoStore = useElementoTipoStore();
const ejecutivoStore = useEjecutivoStore();
const usabilidadStore = useUsabilidadStore();
const procesoStore = useProcesoStore();
const prospectStore = useProspectStore();
const usuario = authStore.usuario();
const { filters } = storeToRefs(priceStore);
const { emptyList, findAllCotizacionesHTTP, setPage, resetFilter } = priceStore;
const { searchProspectsHTTP } = prospectStore;

const defaultFilters = JSON.parse(JSON.stringify(prices_filters_model));

const esEjecutivoDeVentas = computed(() => usuario.roles.some((rol) => rol.Codigo === 1));

const formatearFechaParaUI = (val) => {
  if (!val) return null;
  const d = new Date(val);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const filterSchema = computed(() => [
  {
    key: 'FechaCreacionRange',
    label: 'Fecha Cotización',
    type: 'date_range',
    keyDesde: 'FechaDesde',
    keyHasta: 'FechaHasta',
    fixed: true,
    span: 6,
  },
  {
    key: 'CodigoUsuarioCreacion',
    label: 'Ejecutivo',
    type: 'select',
    placeholder: 'Seleccionar un ejecutivo',
    fetchOptions: async () => {
      const results = await ejecutivoStore.searchEjecutivosHTTP('');
      return (results || []).map((u) => ({
        ...u,
        NombreCompleto: `${u.Nombre || ''} ${u.Apellido || ''}`.trim(),
      }));
    },
    fieldKey: 'Codigo',
    fieldLabel: 'NombreCompleto',
    span: 4,
    fixed: esEjecutivoDeVentas.value,
    disabled: esEjecutivoDeVentas.value,
  },
  {
    key: 'EstadoCotizacion',
    label: 'Estado',
    type: 'select',
    placeholder: 'Seleccionar estado',
    options: estadoSolicitudStore.estadoSolicitudList,
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 4,
  },
  {
    key: 'Cliente',
    label: 'Cliente',
    type: 'autocomplete',
    placeholder: 'Ingresar cliente',
    fieldLabel: 'Descripcion',
    searchFn: async (query) => {
      if (query && query.length >= 3 && query !== 'null') {
        const results = await searchProspectsHTTP(query);

        return results.map((item) => ({
          ...item,
          value: item.Descripcion,
        }));
      }
      return [];
    },
    span: 4,
  },
  {
    key: 'CodigoEspecificacionTecnica',
    label: 'Cód. ET',
    type: 'input',
    placeholder: 'Ej: 3401, 3402',
    span: 3,
  },
  {
    key: 'CodigoEquivalente',
    label: 'Cód. Equivalente',
    type: 'input',
    placeholder: 'Ej: 123-A',
    span: 3,
  },
  {
    key: 'ElementoTipo',
    label: 'Tipo de producto',
    type: 'select',
    placeholder: 'Seleccionar',
    options: elementoTipoStore.productTypeList,
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    multiple: true,
    span: 4,
  },
  {
    key: 'FechaEstadoRange',
    label: 'Fecha Estado',
    type: 'date_range',
    keyDesde: 'FechaEstadoDesde',
    keyHasta: 'FechaEstadoHasta',
    span: 6,
  },
  {
    key: 'Referencia',
    label: 'Producto',
    type: 'input',
    placeholder: 'Ingresar producto',
    span: 4,
  },
  {
    key: 'CodigoAdicional',
    label: 'Cód. Adicional',
    type: 'input',
    placeholder: 'Ej: SK-900',
    span: 4,
  },
  {
    key: 'DescripcionComercial',
    label: 'Descripción Material',
    type: 'input',
    placeholder: 'Ej: térmico, bpp',
    span: 4,
  },
  {
    key: 'CodigoUsabilidad',
    label: 'Usabilidad',
    type: 'select',
    placeholder: 'Seleccionar usabilidad',
    fetchOptions: async () => {
      await usabilidadStore.findAllUsabilidadHTTP({ page: 0 });
      return usabilidadStore.usabilidadLista;
    },
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 4,
  },
  {
    key: 'Suaje',
    label: 'Suaje',
    type: 'input',
    placeholder: 'Ej: RM228',
    span: 4,
  },
  {
    key: 'Procesos',
    label: 'Procesos',
    type: 'select',
    placeholder: 'Seleccionar procesos',
    multiple: true,
    fetchOptions: async () => {
      try {
        await procesoStore.findAllProcesosHTTP({ page: 0, Activo: true });
        return procesoStore.procesoList || [];
      } catch (e) {
        return [];
      }
    },
    fieldKey: 'Codigo',
    fieldLabel: 'Descripcion',
    span: 5,
  },
  {
    key: 'CodigoCotizacion',
    label: 'Nro. Cotización',
    type: 'input',
    placeholder: 'Ej: 100, 101',
    span: 3,
  },
]);

const smartFiltersModel = computed({
  get: () => {
    const sf = {};
    filterSchema.value.forEach((f) => {
      if (f.type === 'date_range') {
        const desde = filters.value[f.keyDesde]
          ? formatearFechaParaUI(filters.value[f.keyDesde])
          : null;
        const hasta = filters.value[f.keyHasta]
          ? formatearFechaParaUI(filters.value[f.keyHasta])
          : null;
        if (f.fixed || desde || hasta) {
          sf[f.key] = { field: f, value: [desde, hasta] };
        }
      } else if (f.key === 'CodigoUsuarioCreacion') {
        if (
          filters.value.CodigoUsuarioCreacion !== null &&
          filters.value.CodigoUsuarioCreacion !== undefined
        ) {
          const nombreAExhibir = esEjecutivoDeVentas.value
            ? `${usuario.Nombre} ${usuario.Apellido}`.trim()
            : '';
          sf[f.key] = {
            field: f,
            value: { Codigo: filters.value.CodigoUsuarioCreacion, NombreCompleto: nombreAExhibir },
          };
        }
      } else if (f.key === 'EstadoCotizacion') {
        const val = filters.value.EstadoCotizacion;
        if (val && val.Codigo !== null && val.Codigo !== undefined) {
          sf[f.key] = { field: f, value: val };
        }
      } else if (f.key === 'Cliente') {
        if (filters.value.Cliente) {
          sf[f.key] = { field: f, value: { Descripcion: filters.value.Cliente } };
        }
      } else if (f.key === 'ElementoTipo' || f.key === 'Procesos') {
        if (filters.value[f.key] && filters.value[f.key].length > 0) {
          sf[f.key] = { field: f, value: filters.value[f.key] };
        }
      } else if (f.key === 'CodigoUsabilidad') {
        if (
          filters.value.CodigoUsabilidad !== null &&
          filters.value.CodigoUsabilidad !== undefined
        ) {
          sf[f.key] = { field: f, value: { Codigo: filters.value.CodigoUsabilidad } };
        }
      } else {
        if (
          filters.value[f.key] !== null &&
          filters.value[f.key] !== undefined &&
          filters.value[f.key] !== ''
        ) {
          sf[f.key] = { field: f, value: filters.value[f.key] };
        }
      }
    });
    return sf;
  },
  set: (newSf) => {
    const baseEstructura = {
      FechaDesde: null,
      FechaHasta: null,
      EstadoCotizacion: null,
      CodigoUsuarioCreacion: null,
      FechaEstadoDesde: null,
      FechaEstadoHasta: null,
      Cliente: null,
      Referencia: null,
      ElementoTipo: [],
      CodigoEjecutivoCuenta: null,
      AllCotizaciones: true,
      CodigoEquivalente: null,
      CodigoAdicional: null,
      DescripcionComercial: null,
      CodigoProducto: null,
      CodigoETBase: null,
      CodigoUsabilidad: null,
      Suaje: null,
      Procesos: [],
      CodigoCotizacion: null,
    };

    Object.values(newSf).forEach((item) => {
      if (!item || !item.field) return;

      if (item.field.type === 'date_range') {
        baseEstructura[item.field.keyDesde] = item.value?.[0]
          ? new Date(`${item.value[0]}T00:00:00`)
          : null;
        baseEstructura[item.field.keyHasta] = item.value?.[1]
          ? new Date(`${item.value[1]}T23:59:59`)
          : null;
      } else if (
        item.field.key === 'CodigoUsuarioCreacion' ||
        item.field.key === 'CodigoUsabilidad'
      ) {
        const val = item.value;
        baseEstructura[item.field.key] =
          typeof val === 'object' && val !== null ? val.Codigo : (val ?? null);
      } else if (item.field.key === 'Cliente') {
        const val = item.value;
        baseEstructura.Cliente =
          typeof val === 'object' && val !== null ? val.Descripcion : (val ?? null);
      } else if (item.field.key === 'ElementoTipo' || item.field.key === 'Procesos') {
        baseEstructura[item.field.key] = Array.isArray(item.value) ? item.value : [];
      } else {
        baseEstructura[item.field.key] = item.value;
      }
    });

    if (esEjecutivoDeVentas.value) {
      baseEstructura.CodigoUsuarioCreacion = usuario.Codigo;
    }

    for (const key in baseEstructura) {
      priceStore.filters[key] = baseEstructura[key];
    }
  },
});

const filtrarCotizacion = () => {
  setPage(1);
  emptyList();
  findAllCotizacionesHTTP();
};

const LimpiarFiltros = () => {
  resetFilter();
  priceStore.filters.AllCotizaciones = true;
  if (esEjecutivoDeVentas.value) {
    priceStore.filters.CodigoUsuarioCreacion = usuario.Codigo;
  }
  filtrarCotizacion();
};

onBeforeMount(async () => {
  estadoSolicitudStore.firstEstadoSolicitud();
  elementoTipoStore.resetElementoTipoList();

  if (esEjecutivoDeVentas.value && !filters.value.CodigoUsuarioCreacion) {
    priceStore.filters.CodigoUsuarioCreacion = usuario.Codigo;
  }
});
</script>
